<template>
  <div class="detail-banner" :class="{ 'locale-zh-CN': isZhLocale }" :style="{ backgroundImage: `url(${backgroundImage})` }">
    <div class="banner-content">
      <div class="banner-left">
        <slot />
      </div>
      <div v-if="showCollectInfo" class="banner-right">
        <div
          class="banner-stats"
          :class="{ clickable: enableCollectAction, collected: isCollected }"
          @click="handleCollectClick"
        >
          <img class="collect-icon" :src="safeCollectIcon" alt="" @error="onIconError" />
          <span class="stat-label">{{ collectLabel }}</span>
          <span class="stat-divider">|</span>
          <span class="stat-label">{{ collectedLabel }}</span>
          <span class="stat-value">{{ collectCount }}{{ collectUnit }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { localeImg } from '@/utils/localeAsset'
import { getSafeImage, handleImgError } from '@/utils/imageFallback'
import { getText } from '@/utils/i18nText'

export default {
  name: 'DetailBanner',
  props: {
    backgroundImage: {
      type: String,
      required: true
    },
    collectLabel: {
      type: String,
      default: () => getText('detailBanner.collectLabel', '收藏')
    },
    collectedLabel: {
      type: String,
      default: () => getText('detailBanner.collectedLabel', '已收藏：')
    },
    collectCount: {
      type: [String, Number],
      default: 0
    },
    collectUnit: {
      type: String,
      default: () => getText('detailBanner.collectUnit', '次')
    },
    collectIcon: {
      type: String,
      default: () => localeImg('fav-ico.png')
    },
    // 已收藏态图标（默认使用填充图标）
    collectedIcon: {
      type: String,
      default: () => localeImg('fav-full.png')
    },
    showCollectInfo: {
      type: Boolean,
      default: true
    },
    // 是否已收藏（用于填充态展示 + 点击触发取消收藏）
    isCollected: {
      type: Boolean,
      default: false
    },
    // 是否启用“点击收藏”交互（点击后向父组件 emit collect）
    enableCollectAction: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    safeCollectIcon() {
      const icon = this.isCollected ? this.collectedIcon : this.collectIcon
      return getSafeImage(icon)
    }
  },
  methods: {
    handleCollectClick() {
      if (!this.enableCollectAction) return
      const nextCollected = !this.isCollected
      // 兼容旧用法：仍然发 collect，但携带下一态
      this.$emit('collect', { nextCollected, isCollected: this.isCollected })
      // 语义化事件：已收藏时点击触发取消收藏
      if (this.isCollected) this.$emit('cancel')
    },
    onIconError(event) {
      handleImgError(event)
    }
  }
}
</script>

<style lang="scss" scoped>
.detail-banner {
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  border-radius: 8px;
  padding: 40px;
  margin-bottom: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

.banner-content {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 24px;
}

.banner-left {
  flex: 1;
  min-width: 0;
}

.banner-right {
  background: #ffffff;
  border-radius: 20px;
  padding: 10px 20px;
  display: flex;
  align-items: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  align-self: flex-end; /* 收藏信息贴近底部 */
}

.banner-stats {
  display: flex;
  align-items: center;
  gap: 6px;

  &.clickable {
    cursor: pointer;
    user-select: none;
  }

  &.collected {
    .collect-icon {
      /* 没有单独“填充”图标时，用色彩增强模拟填充态 */
      filter: saturate(1.2) brightness(0.95);
    }

    .stat-value {
      color: #8c7b21;
    }
  }

  .collect-icon {
    width: 18px;
    height: 18px;
  }

  .stat-label {
    font-family: Source Han Sans CN;
    font-size: 12px;
    color: #666666;
  }

  .stat-divider {
    color: #dddddd;
    margin: 0 2px;
  }

  .stat-value {
    font-family: Source Han Sans CN;
    font-size: 12px;
    color: #e54c3c;
    font-weight: 500;
  }
}
</style>

<style lang="scss">
.detail-banner.locale-zh-CN {
  .banner-stats {
    .stat-label,
    .stat-value {
      font-size: 16px;
    }
  }
}
</style>


